/**
 * Trace graph layout — layer-based, no external deps.
 *
 * Computes a "distance from anchor" for each node by BFS along the
 * edges (treating them as undirected for layout purposes — we want
 * upstream nodes on the LEFT and downstream on the RIGHT, but
 * unreachable-via-edges nodes shouldn't disappear).
 *
 * ## Visual grouping (two complementary boxes)
 *
 * 1. **Execution boxes** — when an ``execution`` has at least one
 *    contained ``node_execution`` in the trace, the execution + all
 *    its node-execs are pulled into ONE box labelled with the workflow
 *    name. The contained children are reassigned to the execution's
 *    layer (they no longer claim their own column), and the "contains"
 *    edges between them are marked internal so the canvas can hide
 *    them — the visual containment already conveys the relationship.
 *
 * 2. **Workflow boxes** — fallback for the case where ≥2 sibling
 *    executions of the SAME workflow sit in the same layer with no
 *    children visible (typical for a retry chain or a fanout where
 *    the trace was anchored upstream and depth=1 didn't include the
 *    node-execs). Boxes them together so the operator doesn't see a
 *    column of identical-looking cards.
 *
 * The two rules don't overlap: an execution either has children
 * (→ execution box) or doesn't (→ candidate for workflow box).
 */
import type { TraceEdge, TraceNode } from "@/lib/api-trace";

/** Column slot width. Boxes + standalone cards anchor LEFT of this
 *  slot, so the extra width over GROUP_WIDTH becomes horizontal
 *  breathing room between adjacent columns. */
const COLUMN_WIDTH = 420;
/** Visual width of a group box and of standalone cards' "logical"
 *  slot inside the column. */
const GROUP_WIDTH = 280;
const ROW_HEIGHT = 120;
/** Header height when the box is EXPANDED. Needs to be tall enough
 *  to render the header button (text-xs + py-2.5 ≈ 36px) AND leave
 *  vertical breathing room before the first child card — otherwise
 *  the chevron row visually touches/overlaps the child above it. */
const GROUP_HEADER_HEIGHT = 52;
/** Total box height when COLLAPSED — the box becomes a standalone
 *  card-like affordance, so it deserves real card proportions rather
 *  than a thin sliver. */
const GROUP_COLLAPSED_HEIGHT = 60;
const GROUP_PAD_BOTTOM = 12;
const GROUP_PAD_X = 12;
/** Gap between adjacent items inside the same column. */
const COLUMN_GAP = 24;
/** Execution groups with more children than this collapse by default
 *  — keeps the canvas readable for fat workflows. The user can expand
 *  manually via the box header chevron. */
const COLLAPSE_THRESHOLD = 3;

export interface LayoutedNode extends TraceNode {
  position: { x: number; y: number };
  /** Distance from anchor — negative = upstream, positive = downstream. */
  layer: number;
  /** When set, this node is a child of a group; position is RELATIVE
   *  to the group's top-left. */
  parentNode?: string;
}

/** Synthetic group node emitted by the layout. Has no equivalent on
 *  the API contract — pure visual scaffold. */
export interface LayoutedGroup {
  id: string;
  /** Display label, e.g. ``Sync orders`` or ``Sync orders · ×3``. */
  label: string;
  /** ``"execution"`` = exec + its node-execs in one box.
   *  ``"workflow"`` = ≥2 sibling executions of the same workflow. */
  variant: "execution" | "workflow";
  /** Optional sub-label (status, count, etc.) shown to the right. */
  badge?: string;
  /** ``"succeeded"`` / ``"failed"`` / ``undefined`` — used by the
   *  renderer to color the header. */
  status?: string;
  layer: number;
  position: { x: number; y: number };
  width: number;
  height: number;
  /** Ids of child nodes inside the box (for testing, for edge
   *  re-routing when collapsed, and for ``defaultCollapsed`` heuristics). */
  childIds: string[];
  /** When true, children are NOT emitted into ``nodes`` and the box
   *  height is collapsed to just the header. Edges that touched a
   *  hidden child get re-routed to the group via ``collapsedChildToGroup``. */
  collapsed: boolean;
}

export interface LayoutResult {
  nodes: LayoutedNode[];
  groups: LayoutedGroup[];
  /** Edge keys that should be HIDDEN by the canvas — they're now
   *  implicit in the visual containment. Key format:
   *  ``"<source>->_<target>:<kind>"``. */
  hiddenEdgeKeys: Set<string>;
  /** Maps a child-node id to its parent group id when the group is
   *  collapsed. The canvas uses this to rewrite edge endpoints so
   *  they terminate on the visible box instead of a hidden child. */
  collapsedChildToGroup: Map<string, string>;
}

export interface LayoutOptions {
  /** Group ids the user has EXPANDED. Overrides the default-collapse
   *  heuristic. Always wins over ``collapsedGroupIds`` for the same id. */
  expandedGroupIds?: Set<string>;
  /** Group ids the user has explicitly COLLAPSED. Overrides the
   *  default for small groups. */
  collapsedGroupIds?: Set<string>;
}

/** Backward-compatible wrapper for callers that don't care about
 *  groups yet. Returns the flattened node list only. */
export function layoutTraceGraph(
  nodes: TraceNode[],
  edges: TraceEdge[],
  anchorId: string,
): LayoutedNode[] {
  return layoutTraceGraphWithGroups(nodes, edges, anchorId).nodes;
}

export function layoutTraceGraphWithGroups(
  nodes: TraceNode[],
  edges: TraceEdge[],
  anchorId: string,
  options: LayoutOptions = {},
): LayoutResult {
  if (nodes.length === 0) {
    return {
      nodes: [],
      groups: [],
      hiddenEdgeKeys: new Set(),
      collapsedChildToGroup: new Map(),
    };
  }
  const userExpanded = options.expandedGroupIds ?? new Set<string>();
  const userCollapsed = options.collapsedGroupIds ?? new Set<string>();
  const collapsedChildToGroup = new Map<string, string>();
  /** Single point of truth: ``expanded`` wins, then explicit ``collapsed``,
   *  then the default heuristic based on child count. */
  const isCollapsed = (groupId: string, childCount: number): boolean => {
    if (userExpanded.has(groupId)) return false;
    if (userCollapsed.has(groupId)) return true;
    return childCount > COLLAPSE_THRESHOLD;
  };

  // ---------------------------------------------------------------
  // 1. Layer assignment via undirected BFS.
  // ---------------------------------------------------------------
  const upstream = new Map<string, string[]>();
  const downstream = new Map<string, string[]>();
  for (const e of edges) {
    downstream.set(e.source, [...(downstream.get(e.source) ?? []), e.target]);
    upstream.set(e.target, [...(upstream.get(e.target) ?? []), e.source]);
  }

  const layers = new Map<string, number>();
  layers.set(anchorId, 0);
  const queue: string[] = [anchorId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLayer = layers.get(current)!;
    for (const up of upstream.get(current) ?? []) {
      if (!layers.has(up)) {
        layers.set(up, currentLayer - 1);
        queue.push(up);
      }
    }
    for (const down of downstream.get(current) ?? []) {
      if (!layers.has(down)) {
        layers.set(down, currentLayer + 1);
        queue.push(down);
      }
    }
  }
  const maxLayer = Math.max(0, ...Array.from(layers.values()));
  for (const n of nodes) {
    if (!layers.has(n.id)) layers.set(n.id, maxLayer + 2);
  }

  // ---------------------------------------------------------------
  // 2. Discover execution → node_executions relationships via the
  //    "contains" edges. A node-exec only gets absorbed into its
  //    parent if BOTH endpoints are in the trace (otherwise we'd
  //    silently drop nodes whose parent wasn't loaded).
  // ---------------------------------------------------------------
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const childrenByExec = new Map<string, TraceNode[]>();
  const hiddenEdgeKeys = new Set<string>();

  for (const e of edges) {
    if (e.kind !== "contains") continue;
    const src = nodeById.get(e.source);
    const tgt = nodeById.get(e.target);
    if (!src || !tgt) continue;
    if (src.kind !== "execution" || tgt.kind !== "node_execution") continue;
    const list = childrenByExec.get(e.source) ?? [];
    list.push(tgt);
    childrenByExec.set(e.source, list);
    // Edge becomes internal — hide it from the canvas.
    hiddenEdgeKeys.add(`${e.source}->${e.target}:${e.kind}`);
  }

  // Sort each child group by node_id for stable ordering, then by
  // started_at if available (chronological reads better than
  // alphabetical for a workflow trace).
  for (const arr of childrenByExec.values()) {
    arr.sort((a, b) => {
      const ai = (a.data as { iteration?: number }).iteration ?? 0;
      const bi = (b.data as { iteration?: number }).iteration ?? 0;
      if (ai !== bi) return ai - bi;
      const an = (a.data as { node_id?: string }).node_id ?? a.id;
      const bn = (b.data as { node_id?: string }).node_id ?? b.id;
      return an.localeCompare(bn);
    });
  }

  // ---------------------------------------------------------------
  // 3. The absorbed node-execs no longer live in their original
  //    layer — they belong to the parent execution's column.
  //    Build the "claimed children" set so we can skip them when
  //    bucketing standalone nodes.
  // ---------------------------------------------------------------
  const claimedChildIds = new Set<string>();
  for (const children of childrenByExec.values()) {
    for (const c of children) claimedChildIds.add(c.id);
  }

  // ---------------------------------------------------------------
  // 4. Bucket the remaining (unclaimed) nodes by layer for the
  //    standalone + workflow-grouping pass.
  // ---------------------------------------------------------------
  const byLayer = new Map<number, TraceNode[]>();
  for (const n of nodes) {
    if (claimedChildIds.has(n.id)) continue;
    const l = layers.get(n.id)!;
    if (!byLayer.has(l)) byLayer.set(l, []);
    byLayer.get(l)!.push(n);
  }
  for (const arr of byLayer.values()) {
    arr.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
      return a.id.localeCompare(b.id);
    });
  }

  // ---------------------------------------------------------------
  // 5. Compose each column's items.
  // ---------------------------------------------------------------
  type ColumnItem =
    | { type: "execGroup"; exec: TraceNode; children: TraceNode[]; height: number }
    | { type: "wfGroup"; workflowId: string; execs: TraceNode[]; height: number }
    | { type: "node"; node: TraceNode };

  const outNodes: LayoutedNode[] = [];
  const outGroups: LayoutedGroup[] = [];

  // ---------------------------------------------------------------
  // 5a. Layer compaction.
  //
  // When node_executions get absorbed into their parent's execGroup,
  // they no longer occupy their original BFS layer. Anything DOWNSTREAM
  // of them (e.g. a delivery emitted by an absorbed node_exec) still
  // sits at the BFS-computed layer, leaving an EMPTY column between
  // the parent box and the delivery → the "emitted" arrow is visibly
  // 2× longer than the others.
  //
  // Fix: renumber the populated layers densely, preserving sign so
  // negative-layer (upstream) nodes stay on the left of the anchor.
  // ---------------------------------------------------------------
  const populatedLayers = new Set<number>();
  for (const [layer, arr] of byLayer) {
    if (arr.length > 0) populatedLayers.add(layer);
  }
  const compact = new Map<number, number>();
  compact.set(0, 0);
  const posLayers = [...populatedLayers].filter((l) => l > 0).sort((a, b) => a - b);
  posLayers.forEach((l, i) => compact.set(l, i + 1));
  const negLayers = [...populatedLayers].filter((l) => l < 0).sort((a, b) => b - a);
  negLayers.forEach((l, i) => compact.set(l, -(i + 1)));

  for (const [layer, arr] of byLayer) {
    const compactLayer = compact.get(layer) ?? layer;
    const x = compactLayer * COLUMN_WIDTH;

    // Split: executions with children (→ execGroup) vs. childless
    // executions (→ candidates for workflow grouping) vs. everything
    // else (other kinds, standalone).
    const childlessExecsByWorkflow = new Map<string, TraceNode[]>();
    const others: TraceNode[] = [];
    const execGroups: ColumnItem[] = [];

    for (const n of arr) {
      if (n.kind === "execution" && childrenByExec.has(n.id)) {
        const children = childrenByExec.get(n.id)!;
        const groupId = `group:execution:${n.id}`;
        // childCount for the threshold = number of node-execs only
        // (the execution itself isn't a "node" the user wants hidden).
        const collapsed = isCollapsed(groupId, children.length);
        const height = collapsed
          ? GROUP_COLLAPSED_HEIGHT
          : GROUP_HEADER_HEIGHT +
            ROW_HEIGHT + // the execution card itself
            children.length * ROW_HEIGHT +
            GROUP_PAD_BOTTOM;
        execGroups.push({ type: "execGroup", exec: n, children, height });
      } else if (n.kind === "execution") {
        const wfid = (n.data as { workflow_id?: string }).workflow_id;
        if (wfid) {
          (childlessExecsByWorkflow.get(wfid) ??
            childlessExecsByWorkflow.set(wfid, []).get(wfid)!).push(n);
        } else {
          others.push(n);
        }
      } else {
        others.push(n);
      }
    }

    const items: ColumnItem[] = [];
    // execGroups first (they're the most informative), sorted by
    // workflow name for stability.
    execGroups.sort((a, b) => {
      const an = nameOfExec(a.type === "execGroup" ? a.exec : null);
      const bn = nameOfExec(b.type === "execGroup" ? b.exec : null);
      return an.localeCompare(bn);
    });
    items.push(...execGroups);

    // Then workflow groups (≥2 childless executions of the same wf).
    const wfGroupItems: ColumnItem[] = [];
    for (const [wfid, execs] of childlessExecsByWorkflow) {
      if (execs.length >= 2) {
        const groupId = `group:workflow:${wfid}:${layer}`;
        const collapsed = isCollapsed(groupId, execs.length);
        const height = collapsed
          ? GROUP_COLLAPSED_HEIGHT
          : GROUP_HEADER_HEIGHT + execs.length * ROW_HEIGHT + GROUP_PAD_BOTTOM;
        wfGroupItems.push({ type: "wfGroup", workflowId: wfid, execs, height });
      } else {
        // Lone childless execution → standalone.
        others.push(execs[0]);
      }
    }
    wfGroupItems.sort((a, b) => {
      const an =
        (a.type === "wfGroup" &&
          (a.execs[0].data as { workflow_name?: string }).workflow_name) ||
        "";
      const bn =
        (b.type === "wfGroup" &&
          (b.execs[0].data as { workflow_name?: string }).workflow_name) ||
        "";
      return an.localeCompare(bn);
    });
    items.push(...wfGroupItems);

    // Standalone nodes last, in their pre-sorted order.
    others.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
      return a.id.localeCompare(b.id);
    });
    for (const n of others) items.push({ type: "node", node: n });

    // ---------------------------------------------------------------
    // 6. Vertical packing — centre the stack on y=0.
    // ---------------------------------------------------------------
    const totalHeight =
      items.reduce(
        (acc, it) =>
          acc +
          (it.type === "node" ? ROW_HEIGHT : it.height),
        0,
      ) +
      Math.max(0, items.length - 1) * COLUMN_GAP;
    let cursorY = -totalHeight / 2;

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.type === "execGroup") {
        const groupId = `group:execution:${it.exec.id}`;
        const wfName = nameOfExec(it.exec);
        const status = (it.exec.data as { status?: string }).status;
        const collapsed = isCollapsed(groupId, it.children.length);
        outGroups.push({
          id: groupId,
          label: wfName,
          variant: "execution",
          badge: `${it.children.length} ${it.children.length === 1 ? "node" : "nodes"}`,
          status,
          layer,
          position: { x, y: cursorY },
          width: GROUP_WIDTH,
          height: it.height,
          childIds: [it.exec.id, ...it.children.map((c) => c.id)],
          collapsed,
        });
        if (collapsed) {
          // Re-route every external edge that referenced any child
          // (execution or node-exec) to the visible group box.
          collapsedChildToGroup.set(it.exec.id, groupId);
          for (const c of it.children) {
            collapsedChildToGroup.set(c.id, groupId);
          }
        } else {
          // Execution at the top of the box, then node-execs below.
          outNodes.push({
            ...it.exec,
            layer,
            parentNode: groupId,
            position: { x: GROUP_PAD_X, y: GROUP_HEADER_HEIGHT },
          });
          it.children.forEach((child, idx) => {
            outNodes.push({
              ...child,
              layer,
              parentNode: groupId,
              position: {
                x: GROUP_PAD_X,
                y: GROUP_HEADER_HEIGHT + ROW_HEIGHT + idx * ROW_HEIGHT,
              },
            });
          });
        }
        cursorY += it.height;
      } else if (it.type === "wfGroup") {
        const groupId = `group:workflow:${it.workflowId}:${layer}`;
        const wfName =
          (it.execs[0].data as { workflow_name?: string }).workflow_name ??
          it.workflowId.slice(0, 8);
        const collapsed = isCollapsed(groupId, it.execs.length);
        outGroups.push({
          id: groupId,
          label: wfName,
          variant: "workflow",
          badge: `×${it.execs.length}`,
          layer,
          position: { x, y: cursorY },
          width: GROUP_WIDTH,
          height: it.height,
          childIds: it.execs.map((e) => e.id),
          collapsed,
        });
        if (collapsed) {
          for (const c of it.execs) {
            collapsedChildToGroup.set(c.id, groupId);
          }
        } else {
          it.execs.forEach((child, idx) => {
            outNodes.push({
              ...child,
              layer,
              parentNode: groupId,
              position: {
                x: GROUP_PAD_X,
                y: GROUP_HEADER_HEIGHT + idx * ROW_HEIGHT,
              },
            });
          });
        }
        cursorY += it.height;
      } else {
        outNodes.push({
          ...it.node,
          layer,
          position: { x, y: cursorY },
        });
        cursorY += ROW_HEIGHT;
      }
      if (i < items.length - 1) cursorY += COLUMN_GAP;
    }
  }

  return {
    nodes: outNodes,
    groups: outGroups,
    hiddenEdgeKeys,
    collapsedChildToGroup,
  };
}

function nameOfExec(exec: TraceNode | null): string {
  if (!exec) return "";
  return (
    (exec.data as { workflow_name?: string }).workflow_name ??
    `execution ${exec.id.slice(0, 8)}`
  );
}
